<div class="container-fluid px-4 py-3" @click="close_dropdown">
  <!-- Header -->
  <header class="mb-4">
    <h3 class="fw-bold text-xs mb-1">Settings</h3>
    <p class="text-muted text-xs mb-0">Manage team access and user privileges</p>
  </header>

  <div class="mb-4">
    <h3 class="fw-semibold text-xs mb-1">Team Members</h3>
    <p class="text-muted text-xs mb-0">
      {{ team_members.length }} members
      ({{ active_members_count }} active, {{ invited_members_count }} invited)
    </p>
  </div>

  <div>
    <div v-if="users_error" class="alert alert-danger border-0 rounded-3 text-xs" role="alert">
      {{ users_error }}
    </div>
    <div v-if="users_success" class="alert alert-success border-0 rounded-3 text-xs" role="alert">
      {{ users_success }}
    </div>

    <!-- Team Members Table -->
    <div class="card border-primary shadow-sm rounded-4">
      <div class="card-body">
        <div class="d-flex justify-content-end mb-4">
          <button class="btn btn-primary btn-sm rounded-3 text-xs" @click="invite_member" :disabled="is_loading_users || is_saving_user">
            <i class="bi bi-plus me-1"></i> Invite Member
          </button>
        </div>

        <div class="table-responsive">
            <table
              class="table table-hover table-sm mb-0 w-100 global_data_table"
              data-page-length="5"
              data-order-column="0"
              data-order-direction="asc"
              data-non-orderable-targets="[4]"
              data-search-placeholder="Search team members"
            >
              <thead class="table-light">
                <tr>
                  <th class="fw-semibold text-xs ps-4">Member</th>
                  <th class="fw-semibold text-xs d-none d-md-table-cell">Email</th>
                  <th class="fw-semibold text-xs">Role</th>
                  <th class="fw-semibold text-xs">Status</th>
                  <th class="fw-semibold text-xs text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="is_loading_users">
                  <td colspan="5" class="text-center text-muted py-4 text-xs">
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading team members...
                  </td>
                </tr>
                <tr v-for="member in team_members" :key="member.id">
                  <!-- Member Column -->
                  <td class="ps-4" data-label="Member">
                    <div class="d-flex align-items-center">
                      <div class="member-avatar rounded-circle d-flex align-items-center justify-content-center me-3"
                           :class="{
                             'bg-primary text-white': member.status === 'active',
                             'bg-warning text-dark': member.status === 'invited',
                             'bg-secondary text-white': member.status === 'inactive'
                           }">
                        {{ get_initials(member.name) }}
                      </div>
                      <div>
                        <div class="fw-semibold text-xs">{{ member.name }}</div>
                        <div class="text-muted text-xs d-md-none">{{ member.email }}</div>
                        <div v-if="member.status === 'invited'" class="text-warning text-xs">
                          <i class="bi bi-envelope me-1"></i> Invitation sent
                        </div>
                      </div>
                    </div>
                  </td>
                  
                  <!-- Email Column -->
                  <td class="d-none d-md-table-cell" data-label="Email">
                    <div class="text-muted text-xs">{{ member.email }}</div>
                  </td>
                  
                  <!-- Role Column -->
                  <td data-label="Role">
                    <span class="badge text-xs"
                          :class="{
                            'bg-primary text-white': member.role === 'Administrator',
                            'bg-info text-white': member.role === 'Manager',
                            'bg-secondary text-white': member.role === 'Analyst',
                            'bg-light text-dark': !['Administrator','Manager','Analyst'].includes(member.role)
                          }">
                      {{ member.role }}
                    </span>
                  </td>
                  
                  <!-- Status Column -->
                  <td data-label="Status">
                    <span class="badge d-inline-flex align-items-center text-xs"
                          :class="{
                            'bg-success-subtle text-success-emphasis': member.status === 'active',
                            'bg-warning-subtle text-warning-emphasis': member.status === 'invited',
                            'bg-secondary-subtle text-secondary-emphasis': member.status === 'inactive'
                          }">
                      <i class="bi bi-circle-fill me-1"
                         :class="{
                           'text-success': member.status === 'active',
                           'text-warning': member.status === 'invited',
                           'text-secondary': member.status === 'inactive'
                         }"
                         style="font-size: 0.5rem;"></i>
                      <span class="text-capitalize">{{ member.status }}</span>
                    </span>
                  </td>
                  
                  <!-- Actions Column -->
                  <td class="text-end pe-4" data-label="Actions">
                    <div class="dropdown">
                      <button class="btn btn-sm btn-outline-secondary rounded-3 text-xs"
                              type="button"
                              @click="toggle_dropdown(member.id, $event)">
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <div class="dropdown-menu dropdown-menu-end shadow rounded-3"
                           :class="{ 'show': open_dropdown_id === member.id }">
                        <a class="dropdown-item text-xs" href="#" @click.prevent="open_change_role_modal(member)">
                          <i class="bi bi-pencil-square me-2 text-primary"></i>
                          Edit User
                        </a>
                        <div class="dropdown-divider"></div>
                        <a class="dropdown-item text-danger text-xs" href="#"
                           @click.prevent="open_delete_modal(member)">
                          <i class="bi bi-person-dash me-2"></i>
                          Deactivate
                        </a>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Create/Edit Role Modal -->
  <div class="modal" :class="{ 'show d-block': show_role_modal }" v-if="show_role_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content rounded-4">
        <div class="modal-header border-bottom">
          <h5 class="modal-title fw-semibold text-xs">
            {{ is_editing_role ? 'Edit Role' : 'Create New Role' }}
          </h5>
          <button type="button" class="btn-close" @click="close_role_modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit_role">
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="role_name" class="form-label text-xs fw-semibold">Role Name *</label>
                <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="role_name"
                       v-model="new_role.name"
                       placeholder="e.g., Administrator"
                       required>
              </div>
              <div class="col-md-6">
                <label for="role_short_name" class="form-label text-xs fw-semibold">Short Name *</label>
                <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="role_short_name"
                       v-model="new_role.short_name"
                       placeholder="e.g., admin"
                       required>
              </div>
            </div>
            
            <div class="mb-3">
              <label for="role_description" class="form-label text-xs fw-semibold">Description *</label>
              <textarea class="form-control form-control-sm rounded-3 text-xs" id="role_description"
                        v-model="new_role.description"
                        rows="2"
                        placeholder="Brief description of the role's purpose"
                        required></textarea>
            </div>
            
            <div class="mb-3">
              <label class="form-label text-xs fw-semibold">Role Color</label>
              <div class="d-flex flex-wrap gap-2">
                <button type="button"
                        v-for="color_option in role_colors"
                        :key="color_option.value"
                        class="btn btn-sm d-flex align-items-center gap-2 rounded-3 text-xs"
                        :class="[
                          new_role.color === color_option.value ? 'btn-primary' : 'btn-outline-secondary',
                          color_option.value === 'light' ? 'text-dark' : ''
                        ]"
                        @click="new_role.color = color_option.value">
                  <span class="rounded-circle d-inline-block"
                        :class="color_option.class"
                        style="width: 16px; height: 16px;"></span>
                  {{ color_option.label }}
                </button>
              </div>
            </div>
            
            <div class="mb-4">
              <label class="form-label text-xs fw-semibold">Permissions *</label>
              <p class="text-muted text-xs mb-2">Select permissions for this role:</p>
              <div class="row">
                <div class="col-md-6" v-for="permission in available_permissions" :key="permission">
                  <div class="form-check mb-2">
                    <input class="form-check-input"
                           type="checkbox"
                           :id="`perm_${permission}`"
                           :checked="is_permission_selected(permission)"
                           @change="toggle_permission(permission)">
                    <label class="form-check-label text-xs" :for="`perm_${permission}`">
                      {{ permission }}
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="new_role.permissions.length === 0" class="alert alert-warning mt-2 rounded-3">
                <i class="bi bi-exclamation-triangle me-2"></i>
                <span class="text-xs">No permissions selected. Users with this role won't be able to access anything.</span>
              </div>
            </div>
            
            <div class="modal-footer border-top">
              <button type="button" class="btn btn-outline-secondary btn-sm rounded-3 text-xs" @click="close_role_modal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary btn-sm rounded-3 text-xs">
                {{ is_editing_role ? 'Update Role' : 'Create Role' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop for Role Modal -->
  <div class="modal-backdrop fade show" v-if="show_role_modal" @click="close_role_modal"></div>

  <!-- Invite Member Modal -->
  <div class="modal" :class="{ 'show d-block': show_invite_modal }" v-if="show_invite_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content rounded-4">
        <div class="modal-header border-bottom">
          <h5 class="modal-title fw-semibold text-xs">Invite Team Member</h5>
          <button type="button" class="btn-close" @click="close_invite_modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit_invite">
            <div class="row mb-3">
              <div class="col-md-6">
                <label for="first_name" class="form-label text-xs fw-semibold">First Name *</label>
                <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="first_name"
                       v-model="new_member.first_name"
                       placeholder="John"
                       required>
              </div>
              <div class="col-md-6">
                <label for="last_name" class="form-label text-xs fw-semibold">Last Name *</label>
                <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="last_name"
                       v-model="new_member.last_name"
                       placeholder="Dlamini"
                       required>
              </div>
            </div>
            
            <div class="mb-3">
              <label for="invite_email" class="form-label text-xs fw-semibold">Email Address *</label>
              <input type="email" class="form-control form-control-sm rounded-3 text-xs" id="invite_email"
                     v-model="new_member.email"
                     placeholder="john.dlamini@company.co.za"
                     required>
            </div>

            <div class="mb-3">
              <label for="invite_phone" class="form-label text-xs fw-semibold">Phone Number</label>
              <input type="tel" class="form-control form-control-sm rounded-3 text-xs" id="invite_phone"
                     v-model="new_member.phone_number"
                     placeholder="+27 11 123 4567">
            </div>
            
            <div class="mb-3">
              <label for="invite_role" class="form-label text-xs fw-semibold">Role *</label>
              <select class="form-select form-select-sm rounded-3 text-xs" id="invite_role" v-model="new_member.role" required :disabled="is_loading_roles">
                <option v-if="is_loading_roles" value="">Loading roles...</option>
                <option v-for="role in roles" :key="role.id" :value="role.name">{{ role.name }}</option>
              </select>
            </div>
            
            
            <div class="modal-footer border-top">
              <button type="button" class="btn btn-outline-secondary btn-sm rounded-3 text-xs" @click="close_invite_modal">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary btn-sm rounded-3 text-xs" :disabled="is_saving_user">
                <span v-if="is_saving_user" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                <i v-else class="bi bi-send me-1"></i> Send Invitation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit User Modal -->
  <div class="modal" :class="{ 'show d-block': show_change_role_modal }" v-if="show_change_role_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content rounded-4">
        <div class="modal-header border-bottom">
          <h5 class="modal-title fw-semibold text-xs">Edit User</h5>
          <button type="button" class="btn-close" @click="close_change_role_modal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submit_change_role">
            <div v-if="selected_member">
              <div class="row mb-3">
                <div class="col-md-6">
                  <label for="edit_first_name" class="form-label text-xs fw-semibold">First Name *</label>
                  <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="edit_first_name"
                         v-model="selected_member.first_name"
                         placeholder="John"
                         required>
                </div>
                <div class="col-md-6">
                  <label for="edit_last_name" class="form-label text-xs fw-semibold">Last Name *</label>
                  <input type="text" class="form-control form-control-sm rounded-3 text-xs" id="edit_last_name"
                         v-model="selected_member.last_name"
                         placeholder="Dlamini"
                         required>
                </div>
              </div>

              <div class="mb-3">
                <label for="edit_email" class="form-label text-xs fw-semibold">Email Address</label>
                <input type="email" class="form-control form-control-sm rounded-3 text-xs" id="edit_email"
                       v-model="selected_member.email"
                       placeholder="john.dlamini@company.co.za"
                       readonly>
              </div>

              <div class="mb-3">
                <label for="edit_phone" class="form-label text-xs fw-semibold">Phone Number</label>
                <input type="tel" class="form-control form-control-sm rounded-3 text-xs" id="edit_phone"
                       v-model="selected_member.phone_number"
                       placeholder="+27 11 123 4567">
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer border-top">
          <button type="button" class="btn btn-outline-secondary btn-sm rounded-3 text-xs" @click="close_change_role_modal">
            Cancel
          </button>
          <button type="button" class="btn btn-primary btn-sm rounded-3 text-xs" @click="submit_change_role" :disabled="is_saving_user">
            <span v-if="is_saving_user" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            <i v-else class="bi bi-check-circle me-1"></i> Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Delete User Modal -->
  <div class="modal" :class="{ 'show d-block': show_delete_modal }" v-if="show_delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content rounded-4">
        <div class="modal-header border-bottom">
          <h5 class="modal-title fw-semibold text-xs text-danger">Deactivate Team Member</h5>
          <button type="button" class="btn-close" @click="close_delete_modal"></button>
        </div>
        <div class="modal-body">
          <div v-if="selected_member" class="text-center">
            <div class="mb-4">
              <div class="modal-avatar rounded-circle d-flex align-items-center justify-content-center mx-auto bg-warning text-dark">
                <i class="bi bi-person-dash"></i>
              </div>
            </div>
            
            <h5 class="fw-semibold text-xs mb-3">Confirm deactivation</h5>
            <p class="text-muted text-xs mb-4">
              You are about to deactivate <strong>{{ selected_member.name }}</strong> and remove access to the dashboard.
            </p>
            
            <div class="alert alert-light rounded-3 mb-3">
              <div class="d-flex align-items-center">
                <div class="modal-avatar rounded-circle d-flex align-items-center justify-content-center me-3 bg-primary text-white">
                  {{ get_initials(selected_member.name) }}
                </div>
                <div>
                  <div class="fw-semibold text-xs">{{ selected_member.name }}</div>
                  <small class="text-muted text-xs">Email: {{ selected_member.email }}</small>
                </div>
              </div>
            </div>
            
            <div class="alert alert-warning rounded-3">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <span class="text-xs"><strong>Warning:</strong> Deactivating this user will remove their access.</span>
            </div>
          </div>
        </div>
        <div class="modal-footer border-top">
          <button type="button" class="btn btn-outline-secondary btn-sm rounded-3 text-xs" @click="close_delete_modal">
            Cancel
          </button>
          <button type="button" class="btn btn-danger btn-sm rounded-3 text-xs" @click="submit_delete" :disabled="is_saving_user">
            <span v-if="is_saving_user" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
            <i v-else class="bi bi-person-dash me-1"></i> Deactivate User
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrops -->
  <div class="modal-backdrop fade show" v-if="show_invite_modal" @click="close_invite_modal"></div>
  <div class="modal-backdrop fade show" v-if="show_change_role_modal" @click="close_change_role_modal"></div>
  <div class="modal-backdrop fade show" v-if="show_delete_modal" @click="close_delete_modal"></div>
